<template>
  <div class="dashboard-container">
    <!-- Welcome Header -->
    <div class="welcome-section">
      <div class="welcome-content">
        <h2 class="welcome-title">Welcome back, {{ userName }}</h2>
        <p class="welcome-subtitle">Here's a quick overview of your extranet ecosystem.</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="row">
      <div class="col-md-3 mb-4" v-for="i in 4" :key="i">
        <div class="skeleton-card">
          <div class="skeleton-icon"></div>
          <div class="skeleton-content">
            <div class="skeleton-text"></div>
            <div class="skeleton-text short"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="error-card">
        <i class="fas fa-exclamation-triangle error-icon"></i>
        <h5 class="error-title">Failed to Load Dashboard</h5>
        <p class="error-message">{{ error }}</p>
        <button class="btn-retry" @click="fetchData">
          <i class="fas fa-redo me-2"></i>Retry
        </button>
      </div>
    </div>

    <!-- Analytics Grid -->
    <div v-else class="analytics-grid">
      <div class="row g-4">
        <!-- Global Section (Left) -->
        <div class="col-lg-7">
          <div class="section-label">Central Analytics</div>
          <div class="row g-3">
            <div class="col-sm-6" v-for="metric in globalMetrics" :key="metric.key">
              <dashboard-card :metric="metric" />
            </div>
          </div>
        </div>

        <!-- Tenant Section (Right) -->
        <div class="col-lg-5">
          <div class="section-label">Tenant Analytics</div>
          
          <div v-if="hasTenantSelected" class="row g-3">
            <div class="col-md-4" v-for="tMetric in tenantMetrics" :key="tMetric.label">
              <div class="tenant-mini-card">
                <div class="mini-card-icon">
                  <i :class="`fas ${tMetric.icon}`"></i>
                </div>
                <div class="mini-card-info">
                  <span class="mini-card-value">{{ tMetric.value.toLocaleString() }}</span>
                  <span class="mini-card-label">{{ tMetric.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Locked State -->
          <div v-else class="locked-tenant-state">
            <div class="locked-content">
              <div class="locked-icon-stack">
                <i class="fas fa-lock locked-main"></i>
              </div>
              <h4 class="locked-title">Tenant Analytics Restricted</h4>
              <p class="locked-text">Please select a tenant to unlock analytics.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Full Tenant Details (Bottom) -->
    <div v-if="!loading && !error && hasTenantSelected" class="row mt-5">
      <div class="col-12">
        <tenant-detail-card :tenant="selectedTenant" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import DashboardCard from '@/components/extranet/DashboardCard.vue'
import TenantDetailCard from '@/components/extranet/TenantDetailCard.vue'

/**
 * ExtranetDashboard Component
 * 
 * Displays cross-tenant aggregated metrics for root admin.
 * Fetches data from /api/extranet/dashboard endpoint.
 * No X-Tenant-Domain header needed (Central DB request).
 * 
 * If a tenant is selected, displays tenant details below statistics.
 * 
 * @component
 */
export default {
  name: 'ExtranetDashboard',

  components: {
    DashboardCard,
    TenantDetailCard
  },

  data() {
    return {
      loading: false,
      error: null
    }
  },

  computed: {
    ...mapState({
      dashboardData: state => state.extranet.dashboardData,
      selectedTenant: state => state.extranet.selectedTenant,
      tenantStats: state => state.extranet.tenantStats,
      userName: state => state.Auth.user?.name || 'Admin'
    }),
    ...mapGetters({
      hasTenantSelected: 'extranet/hasTenantSelected'
    }),

    /**
     * Global metrics for center analytics
     */
    globalMetrics() {
      if (!this.dashboardData) return []
      return [
        {
          key: 'total_tenants',
          label: 'Total Active Tenants',
          value: this.dashboardData.total_tenants || 0,
          description: 'Managed corporate entities',
          icon: 'fa-building',
        },
        {
          key: 'total_members',
          label: 'Global Member Base',
          value: this.dashboardData.total_members || 0,
          description: 'Total users across all tenants',
          icon: 'fa-users',
        }
      ]
    },

    /**
     * Tenant metrics for right section
     * Reads from tenantStats (fetched from /api/extranet/corporate/{id}/stats)
     * not from selectedTenant, because selectedTenant only has identity data.
     */
    tenantMetrics() {
      if (!this.selectedTenant) return []
      return [
        { label: 'Total Member',   value: this.tenantStats?.total_members   || 0, icon: 'fa-users' },
        { label: 'Total Branch',   value: this.tenantStats?.total_branches  || 0, icon: 'fa-map-marker-alt' },
        { label: 'Total Merchant', value: this.tenantStats?.total_merchants || 0, icon: 'fa-store' }
      ]
    }
  },

  watch: {
    /**
     * Re-fetch tenant stats whenever the selected tenant changes.
     * This handles the scenario where the user switches tenant
     * while staying on the dashboard page.
     */
    selectedTenant(newTenant) {
      if (newTenant?.id && newTenant?.tenant_id) {
        this.fetchTenantStats({
          corporateId: newTenant.id,
          tenantId: newTenant.tenant_id
        })
      }
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    ...mapActions({
      fetchDashboard: 'extranet/fetchDashboard',
      fetchTenantStats: 'extranet/fetchTenantStats'
    }),

    /**
     * Fetch global dashboard data from API
     * Also triggers tenant stats if a tenant is already selected
     * (e.g. restored from localStorage on page refresh).
     *
     * @async
     * @returns {Promise<void>}
     */
    async fetchData() {
      this.loading = true
      this.error = null

      try {
        await this.fetchDashboard()

        // If tenant was restored from localStorage, load their stats immediately
        if (this.selectedTenant?.id && this.selectedTenant?.tenant_id) {
          await this.fetchTenantStats({
            corporateId: this.selectedTenant.id,
            tenantId: this.selectedTenant.tenant_id
          })
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 403) {
            this.error = 'Access denied. Root permission required.'
          } else if (error.response.status === 404) {
            this.error = 'Dashboard data not found.'
          } else {
            this.error = error.response.data?.message || 'An error occurred while loading dashboard.'
          }
        } else if (error.request) {
          this.error = 'Network error. Please check your connection.'
        } else {
          this.error = 'An unexpected error occurred.'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Dashboard Layout */
.dashboard-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Welcome Section */
.welcome-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.welcome-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.welcome-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Section Label */
.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 1px;
  margin-bottom: 16px;
}

/* Tenant Cards Grid */
.tenant-mini-card {
  display: flex;
  align-items: start;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid var(--extranet-accent);
  transition: all 0.2s;
  height: 100%;
}

.tenant-mini-card:hover {
  border-color: var(--extranet-primary);
  background: #fff8f8;
}

.mini-card-icon {
  width: 42px;
  height: 42px;
  background: #f8fafc;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--extranet-primary);
  font-size: 16px;
}

.mini-card-info {
  display: flex;
  flex-direction: column;
}

.mini-card-value {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.mini-card-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* Locked State */
.locked-tenant-state {
  height: 100%;
  min-height: 180px;
  border: 2px dashed var(--extranet-accent);
  border-radius: var(--extranet-rounded);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.locked-content {
  text-align: center;
  padding: 32px;
}

.locked-icon-stack {
  margin-bottom: 16px;
  display: inline-flex;
}

.locked-main {
  font-size: 24px;
  color: #cbd5e1;
}

.locked-title {
  font-size: 16px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 4px;
}

.locked-text {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

/* Loading Skeleton */
.skeleton-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid var(--extranet-accent);
  padding: 32px;
  display: flex;
  align-items: center;
  gap: 24px;
  min-height: 140px;
}

.skeleton-icon {
  width: 64px;
  height: 64px;
  border-radius: var(--extranet-rounded);
  background: #f1f5f9;
  flex-shrink: 0;
}

/* Error State */
.error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
}

.error-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid #fee2e2;
  padding: 48px;
  text-align: center;
  max-width: 480px;
  width: 100%;
}

.btn-retry {
  background: var(--extranet-primary);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

/* Info Card (No Tenant selected) */
.info-card {
  background: #f8fafc;
  border-radius: 16px;
  border: 1px dashed #cbd5e1;
  padding: 40px;
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 16px;
}

.info-icon {
  width: 64px;
  height: 64px;
  background: white;
  border: 1px solid var(--extranet-accent);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--extranet-primary);
  font-size: 24px;
  flex-shrink: 0;
}

.info-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.info-text {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 20px;
  }
  
  .welcome-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .info-card {
    flex-direction: column;
    text-align: center;
    padding: 32px;
  }
}
</style>
